
import {defineComponent} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import useRoster, {
  ROSTER_SCOPE_ADMIN,
  ROSTER_STATUS_NOT_ELIGIBLE,
  ROSTER_STATUS_EN_ATTENTE,
  ROSTER_STATUS_COMPLETED,
  ROSTER_STATUS_SIGNEE,
  ROSTER_STATUS_CLOS,
  type RosterStatus,
  type RosterEntry,
} from '@/orangehrmForfaitJoursPlugin/util/composable/useRoster';

export default defineComponent({
  name: 'AdminRoster',
  setup() {
    const roster = useRoster({scope: ROSTER_SCOPE_ADMIN});

    function statusToKey(status: RosterStatus): string {
      switch (status) {
        case ROSTER_STATUS_NOT_ELIGIBLE:
          return 'not_eligible';
        case ROSTER_STATUS_EN_ATTENTE:
          return 'en_attente';
        case ROSTER_STATUS_COMPLETED:
          return 'completed';
        case ROSTER_STATUS_SIGNEE:
          return 'signee';
        case ROSTER_STATUS_CLOS:
          return 'clos';
        default:
          return 'not_eligible';
      }
    }

    function onMonthChange(): void {
      // Reset to page 1 before reloading so the new month starts
      // at the first page (an offset past the new result set would
      // otherwise yield an empty page).
      roster.currentPage.value = 1;
      void roster.load();
    }

    /**
     * A row is clickable for SIGNEE (admin can download PDF).
     * COMPLETED rows are NOT clickable in admin view (admin cannot sign).
     */
    function isRowClickable(entry: RosterEntry): boolean {
      return (
        entry.declarationId !== null && entry.status === ROSTER_STATUS_SIGNEE
      );
    }

    async function downloadPdf(declarationId: number): Promise<void> {
      // @ts-expect-error: appGlobal is not in window object by default
      const baseUrl = window.appGlobal.baseUrl;
      const pdfHttp = new APIService(
        baseUrl,
        `/api/v2/forfait-jours/declarations/${declarationId}/pdf`,
      );
      try {
        const response = await pdfHttp.request({
          method: 'GET',
          params: {kind: 'signed'},
        });
        if (response?.data?.data?.contentBase64) {
          const byteStr = atob(response.data.data.contentBase64);
          const byteArray = new Uint8Array(byteStr.length);
          for (let i = 0; i < byteStr.length; i++) {
            byteArray[i] = byteStr.charCodeAt(i);
          }
          const blob = new Blob([byteArray], {type: 'application/pdf'});
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = response.data.data.filename || 'declaration.pdf';
          a.click();
          URL.revokeObjectURL(url);
        }
      } catch {
        // Silently fail
      }
    }

    function onRowClick(entry: RosterEntry): void {
      if (!isRowClickable(entry) || entry.declarationId === null) {
        return;
      }
      void downloadPdf(entry.declarationId);
    }

    void roster.load();

    return {
      month: roster.month,
      includePastEmployees: roster.includePastEmployees,
      entries: roster.entries,
      isLoading: roster.isLoading,
      isEmpty: roster.isEmpty,
      error: roster.error,
      currentPage: roster.currentPage,
      pages: roster.pages,
      showPaginator: roster.showPaginator,
      statusToKey,
      onMonthChange,
      isRowClickable,
      onRowClick,
    };
  },
});
